import { useQuery } from "@tanstack/react-query";
import { useState } from "react";

import CompanyLogo from "~/components/CompanyLogo";
import Tooltip from "~/components/Tooltip";
import { ClosedMonthPill } from "~/modules/dashboard/components/ClosedMonthPill.tsx";
import { ProjectCardMenu } from "~/modules/dashboard/components/ProjectCardMenu.tsx";
import type { ProjectCardMenuItemData } from "~/modules/dashboard/components/ProjectCardMenuItem.tsx";
import { useDashboardCalendar } from "~/modules/dashboard/hooks/useDashboardCalendar.ts";
import { useDashboardUser } from "~/modules/dashboard/hooks/useDashboardUser.tsx";
import type {
	UsersActiveStaffing,
	UsersActiveStaffingOrder,
	UsersActiveStaffingType,
} from "~/modules/project/api/staffing/staffingTypes.ts";
import { monthlyClosingQueries } from "~/modules/timeTracking/api/monthlyClosing/monthlyClosingQueries.ts";
import type {
	MonthlyClosingCloseRequest,
	MonthlyClosingReopenRequest,
} from "~/modules/timeTracking/api/monthlyClosing/monthlyClosingTypes.ts";
import { CloseMonthlyClosingModal } from "~/modules/timeTracking/components/CloseMonthlyClosingModal.tsx";
import { getStaffingLabel } from "~/modules/timeTracking/components/closingLists/closingListTypes.ts";
import { ReopenMonthlyClosingModal } from "~/modules/timeTracking/components/ReopenMonthlyClosingModal.tsx";
import { formatNumberWithComma } from "~/utils/numberUtils.ts";

const EMPTY_FIGURE = "–";

export type ProjectCardData = {
	project: UsersActiveStaffingType;
	order: UsersActiveStaffingOrder;
	staffing: UsersActiveStaffing;
};

type Props = ProjectCardData;

function formatFigure(value: number | null): string {
	return value === null ? EMPTY_FIGURE : formatNumberWithComma(value, 1, true);
}

// The title stays on one line, so the cards of one row have the same height. The tooltip shows the
// full title that the "…" cuts.
// The card finds its part in the closing read of the month that the calendar shows, with the same key
// as the calendar, so no second request goes out. Without a part (no read yet, or a staffing that the
// read does not list) the card shows nothing about the closing: a guess from other fields would
// contradict the backend.
export function ProjectCard({ project, order, staffing }: Props) {
	const user = useDashboardUser();
	const { month } = useDashboardCalendar();
	const userId = user?.id ?? "";
	const { data: closing } = useQuery(monthlyClosingQueries.month(userId, month));
	const part = closing?.staffings.find((candidate) => candidate.staffingId === staffing.id);

	const [closeRequest, setCloseRequest] = useState<MonthlyClosingCloseRequest | null>(null);
	const [reopenRequest, setReopenRequest] = useState<MonthlyClosingReopenRequest | null>(null);

	const menuItems: ProjectCardMenuItemData[] = [];
	if (part) {
		const label = getStaffingLabel(part, project.title);
		if (part.close) {
			menuItems.push({
				label: "Monat schließen",
				block: part.close,
				onClick: () => setCloseRequest({
					scope: { scope: "staffings", staffingIds: [part.staffingId] },
					label,
				}),
			});
		}
		if (part.reopen) {
			menuItems.push({
				label: "Wieder öffnen",
				block: part.reopen,
				onClick: () => setReopenRequest({ part: { part: "staffing", staffingId: part.staffingId }, label }),
			});
		}
	}

	return (
		<li className="flex min-w-0 flex-col overflow-hidden rounded-lg border border-gray-200 bg-white">
			<div className="flex items-start gap-3 border-b border-gray-200 bg-gray-50 px-3 py-2.5">
				<CompanyLogo image={project.client.avatarImage}
							 displayName={project.client.displayName}
							 sizeClassName="size-9" />
				<div className="min-w-0 flex-1">
					<Tooltip text={project.title}>
						<h4 className="truncate text-sm font-semibold text-gray-900">{project.title}</h4>
					</Tooltip>
					{order.isOverBudget && (
						<p className="text-xs font-medium text-red-600">Budget überschritten</p>
					)}
					{part?.isClosed && <div className="mt-1"><ClosedMonthPill /></div>}
				</div>
				<ProjectCardMenu items={menuItems} />
			</div>
			<dl className="flex flex-col divide-y divide-gray-100 px-3 py-1">
				<div className="flex items-baseline justify-between gap-x-4 py-1.5">
					<dt className="text-sm text-gray-500">Verfügbare Tage</dt>
					<dd className="text-sm font-semibold text-gray-900">{formatFigure(staffing.availableDays)}</dd>
				</div>
				<div className="flex items-baseline justify-between gap-x-4 py-1.5">
					<dt className="text-sm text-gray-500">Geplante Auslastung</dt>
					<dd className="text-sm font-semibold text-gray-900">
						{staffing.plannedDaysPerWeek === null
							? EMPTY_FIGURE
							: `${formatFigure(staffing.plannedDaysPerWeek)} PT/w`}
					</dd>
				</div>
			</dl>
			<CloseMonthlyClosingModal month={month}
									  userId={userId}
									  request={closeRequest}
									  onClose={() => setCloseRequest(null)} />
			<ReopenMonthlyClosingModal month={month}
									   userId={userId}
									   request={reopenRequest}
									   onClose={() => setReopenRequest(null)} />
		</li>
	);
}
